import { createApp } from "vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import * as Sentry from "@sentry/vue";
import messages from "./i18n/messages.json";
import { useDraftStore } from "@/store/draft";
import OneClickLoader from "@/services/payment/oneClickLoader";

import App from "./App.vue";
import "./styles/orderpage.css";

const pinia = createPinia();

const oneClickParams = new URLSearchParams(window.location.search);
if (
  oneClickParams.get("orderMethod") === "oneclick" &&
  oneClickParams.get("addChoices")
) {
  OneClickLoader.start();
  useDraftStore(pinia).setOneClickProcessing(true);
}

const i18n = createI18n({
  locale: window.navigator.language.split("-")[0],
  fallbackLocale: "fr",
  messages,
});

const app = createApp(App).use(pinia).use(i18n);
if (process.env.NODE_ENV === "development") {
  app.config.devtools = true;
}

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    environment: process.env.VUE_APP_SENTRY_ENV,
    integrations: [],

    tracesSampleRate: 0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost"],

    ignoreErrors: [
      "Network Error",
      "timeout exceeded",
      "Failed to fetch",
      "Load failed",
      "NetworkError when attempting to fetch resource",
      "Request aborted",
      "The operation is insecure",
      /Failed to read the 'localStorage' property from 'Window'/,
      /Failed to execute 'sendBeacon'/,
      "navigator.sendBeacon is not a function",
      "Java object is gone",
      /Object Not Found Matching Id/,
      "NS_ERROR_FAILURE",
      "NS_ERROR_ABORT",
      /findLast is not a function/,
      "Can't find variable: HTMLDialogElement",
      "Can't find variable: UC_UI",
      "Can't find variable: SkeepersProductWidget",
      "UC_UI is not defined",
      "SkeepersProductWidget is not defined",
    ],

    denyUrls: [
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-(web-)?extension:\/\//i,
      /web\.cmp\.usercentrics\.eu/,
      /maps\.googleapis\.com/,
      /googletagmanager\.com/,
      /google-analytics\.com/,
      /skeepers/i,
      /\/technical\/grapesjs\//,
      /\/technical\/campaignstats\//,
    ],

    beforeSend(event, hint) {
      if (
        typeof hint?.originalException === "string" &&
        hint?.originalException?.includes("obtp")
      ) {
        return null;
      }
      return event;
    },
  });
}

if (process.env && !window.process) {
  window.process = {
    env: process.env,
  };
}

app.config.globalProperties.renderHTML = function (encodedHTML) {
  if (encodedHTML) {
    try {
      return decodeURIComponent(encodedHTML);
    } catch (e) {
      return encodedHTML;
    }
  } else {
    return "";
  }
};

app.mount("#app");
